import React, { useState } from 'react';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import toast from 'react-hot-toast';

const EvalSettings = ({ evalId, evalData, onRefresh }) => {
  const [loading, setLoading] = useState({
    resetEvaluation: false,
    resetQuestionnaire: false,
    resetChallenge: false,
    delete: false
  });
  
  const { user } = useSession();

  // Console log evalData for debugging
  console.log('EvalSettings evalData:', evalData);

  // Generic function to call the API
  const callEvalSettingsAPI = async (action, data = {}) => {
    try {
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }

      const payload = {
        action: action,
        data: data,
        eval_id: evalId
      };

      const response = await fetch(API_ENDPOINTS.EDIT_EVAL_SETTINGS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log(`${action} result:`, result);
      
      return result;
    } catch (err) {
      console.error(`Error with ${action}:`, err);
      
      if (err.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      } else {
        throw new Error(`Failed to ${action.replace('_', ' ')}. Please try again.`);
      }
    }
  };

  // Reset Evaluation handler (resets both questionnaire and challenge)
  const handleResetEvaluation = async () => {
    setLoading(prev => ({ ...prev, resetEvaluation: true }));
    
    try {
      await callEvalSettingsAPI('reset_evaluation', {
        questionnaire_status: 'qs_not_started',
        challenge_status: 'cs_not_started'
      });
      
      if (onRefresh) {
        onRefresh();
      }
      
      toast.success('The evaluation has been restarted.');
    } catch (error) {
      console.error('Reset evaluation error:', error);
      toast.error(error.message || 'Failed to reset evaluation');
    } finally {
      setLoading(prev => ({ ...prev, resetEvaluation: false }));
    }
  };

  // Reset Questionnaire handler
  const handleResetQuestionnaire = async () => {
    setLoading(prev => ({ ...prev, resetQuestionnaire: true }));
    
    try {
      await callEvalSettingsAPI('reset_questionnaire', {
        questionnaire_status: 'qs_not_started'
      });
      
      if (onRefresh) {
        onRefresh();
      }
      
      toast.success('Questionnaire has been reset to "not started" status');
    } catch (error) {
      console.error('Reset questionnaire error:', error);
      toast.error(error.message || 'Failed to reset questionnaire');
    } finally {
      setLoading(prev => ({ ...prev, resetQuestionnaire: false }));
    }
  };

  // Reset Challenge handler
  const handleResetChallenge = async () => {
    setLoading(prev => ({ ...prev, resetChallenge: true }));
    
    try {
      await callEvalSettingsAPI('reset_challenge', {
        challenge_status: 'cs_not_started'
      });
      
      if (onRefresh) {
        onRefresh();
      }
      
      toast.success('Challenge has been reset to "not started" status');
    } catch (error) {
      console.error('Reset challenge error:', error);
      toast.error(error.message || 'Failed to reset challenge');
    } finally {
      setLoading(prev => ({ ...prev, resetChallenge: false }));
    }
  };

  // Delete Evaluation handler
  const handleDeleteEvaluation = async () => {
    if (!window.confirm('Are you sure you want to delete this evaluation? This action cannot be undone.')) {
      return;
    }
    
    setLoading(prev => ({ ...prev, delete: true }));
    
    try {
      await callEvalSettingsAPI('delete_eval', {
        talent_id: evalData?.talent_id,
        evaluation_talent_id: evalData?.evaluations?.talent_id,
        evaluation_id: evalData?.evaluation_id
      });
      
      // Immediately reload the page since the evaluation no longer exists
      window.location.reload();
    } catch (error) {
      console.error('Delete evaluation error:', error);
      toast.error(error.message || 'Failed to delete evaluation');
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  return (
    <div>
      {/* Tab-related header */}
      <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Evaluation Settings</h2>
          <p className="text-gray-600 mt-2 text-lg">
            Manage the evaluation assigned to this talent.
          </p>
        </div>
      </div>

      {/* Settings Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Reset Actions */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Reset Actions</h3>
          
          <div className="space-y-4">
            {/* Reset Evaluation Section */}
            <div>
              <button
                onClick={handleResetEvaluation}
                disabled={loading.resetEvaluation || loading.resetQuestionnaire || loading.resetChallenge || loading.delete}
                className="w-full px-6 py-3 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading.resetEvaluation ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Resetting...
                  </>
                ) : (
                  'Reset Evaluation'
                )}
              </button>
              <p className="mt-2 text-sm text-gray-600">
                This will restart the full Evaluation for the Talent.
              </p>
            </div>

            {/* Reset Questionnaire Section */}
            <div>
              <button
                onClick={handleResetQuestionnaire}
                disabled={loading.resetQuestionnaire || loading.resetChallenge || loading.delete}
                className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading.resetQuestionnaire ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Resetting...
                  </>
                ) : (
                  'Reset Questionnaire'
                )}
              </button>
              <p className="mt-2 text-sm text-gray-600">
                This will restart the questionnaire.
              </p>
            </div>

            {/* Reset Challenge Section */}
            <div>
              <button
                onClick={handleResetChallenge}
                disabled={loading.resetQuestionnaire || loading.resetChallenge || loading.delete}
                className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading.resetChallenge ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Resetting...
                  </>
                ) : (
                  'Reset Challenge'
                )}
              </button>
              <p className="mt-2 text-sm text-gray-600">
                This will restart the challenge.
              </p>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6">
          <h3 className="text-xl font-bold text-red-600 mb-4">Danger Zone</h3>
          
          <div>
            <button
              onClick={handleDeleteEvaluation}
              disabled={loading.resetQuestionnaire || loading.resetChallenge || loading.delete}
              className="w-full px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {loading.delete ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </>
              ) : (
                'Delete Evaluation'
              )}
            </button>
            <p className="mt-2 text-sm text-gray-600">
              If this evaluation was assigned from a Job or Freeform, the original template will NOT be deleted.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvalSettings;
